import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useEffect } from 'react'
import CreateHealthMonitorModal from '../CreateHealthMonitorModal'
import { deleteHealthMonitor, listOctaviaHealthMonitors, listOctaviaPools } from '../actions'
import { ILoadBalancerDetailsPageTabs } from '../model'
import { octaviaHealthMonitorsSelector, octaviaPoolsSelector } from '../selectors'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import CustomDeleteDialog from 'core/components/CustomDeleteDialog'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.OctaviaHealthMonitors
type SelectorModel = ArrayElement<ReturnType<typeof octaviaHealthMonitorsSelector>>
type ActionParams = InferActionParams<typeof listOctaviaHealthMonitors>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  poolIds: [],
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const Phase = {
  Active: 'Active',
  Error: 'Error',
  Deleting: 'Deleting',
  Inactive: 'Inactive',
  Unknown: 'Unknown',
}

const getPhaseStatus = (phase): StatusCellModel => {
  const normalizedPhase = phase.trim().toLowerCase()
  switch (normalizedPhase) {
    case Phase.Active.toLowerCase():
      return { variant: 'success', label: 'Active' }
    case Phase.Error.toLowerCase():
      return { variant: 'error', label: 'Error' }
    case Phase.Deleting.toLowerCase():
      return { variant: 'error', label: 'Deleting' }
    case Phase.Inactive.toLowerCase():
      return { variant: 'warning', label: 'Inactive' }
    default:
      return { variant: 'unknown', label: Phase.Unknown }
  }
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OctaviaHealthMonitors',
  listTablePrefs,
)

const searchTargets = ['type', 'status', 'delay', 'pool', 'timeout', 'maxRetries']

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const loadBalancerColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'type',
    label: 'Type',
    // width: 'medium',
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: (phase): StatusCellModel => {
        return getPhaseStatus(phase)
      },
    }),
  },
  {
    key: 'delay',
    label: 'Delay (s)',
  },
  {
    key: 'pool',
    label: 'Pool',
  },

  {
    key: 'timeout',
    label: 'Timeout (s)',
  },
  {
    key: 'maxRetries',
    label: 'Max Retries',
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function MonitorsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { prefs } = useScopedPreferences()
  const { reload: reloadOctaviaPools } = useListAction(listOctaviaPools)
  const octaviaPools = useAppSelector(octaviaPoolsSelector)
  const poolIds = octaviaPools.map((pool) => pool.id)
  const { message, loading, reload } = useListAction(listOctaviaHealthMonitors)
  const data = useAppSelector(octaviaHealthMonitorsSelector)

  const DeleteHealthMonitorDialog = useCallback(
    (props) => (
      <CustomDeleteDialog
        {...props}
        deleteAction={deleteHealthMonitor}
        title="Delete Health Monitor(s)"
        resourceLabel="Health Monitors"
        getRowName={(row) => row?.type}
      />
    ),
    [],
  )

  const handleRefresh = useCallback(() => {
    reload(true)
    reloadOctaviaPools(true)
  }, [poolIds])

  useEffect(() => {
    reload(true)
    reloadOctaviaPools(true)
  }, [])
  return (
    <>
      <CreateHealthMonitorModal addRoute={routeManager.routes.openstack.createLoadBalancers} />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OctaviaHealthMonitors}
        label="Monitors"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={data}
        columns={loadBalancerColumns}
        addUrl={routeManager.routes.openstack.createLoadBalancers.path({
          tab: ILoadBalancerDetailsPageTabs.Monitors,
        })}
        addText="Add Health Monitor"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteHealthMonitor}
        DeleteDialogComponent={DeleteHealthMonitorDialog}
        multiSelection
        emptyContent={
          <EmptyContent title={'No monitors'} subTitle={'Create one in order to see details'} />
        }
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
